import { Link } from "react-router-dom";
import { SeoPageShell } from "./SeoPageShell";

const FAQS = [
  ["How many questions are asked in a firefighter oral board?", "It varies. Many panels ask somewhere between five and twelve main questions, sometimes with follow-ups. Some departments publish their format in the job announcement; if not, plan for a range and practice keeping each answer complete but concise."],
  ["Should I memorize my oral board answers word for word?", "No. Memorized scripts tend to sound flat and fall apart when a panel asks a follow-up. Memorize your stories and the structure you use to tell them, then let the wording change each time you practice."],
  ["Are technical firefighting questions common at an entry-level oral board?", "For entry-level positions, many panels focus on character, judgment, motivation and communication rather than tactics. Promotional boards are different and often include scenario and technical questions. Always read the department's own description of the process."],
  ["How can I practice oral board questions out loud?", "Record yourself, practice with a mentor or study partner who will ask follow-ups, or use First Step's mock oral board, where a three-person AI panel asks questions by voice and scores your answers. A free account includes your first full board free, with a summary report."],
];

export default function FirefighterOralBoardQuestions() {
  return (
    <SeoPageShell
      path="/firefighter-oral-board-questions"
      title="Firefighter Oral Board Questions: Common Questions & How to Answer"
      description="Common firefighter oral board questions by category, with example questions, a simple answer structure, and a practice plan for rehearsing out loud before your panel interview."
      h1="Firefighter Oral Board Questions"
      intro="You cannot know the exact questions a panel will ask, but most firefighter oral boards draw from the same handful of categories. Learn the categories, prepare real stories for each, and practice saying them out loud, and very few questions will surprise you."
      faqs={FAQS}
      testId="seo-oral-board-questions-page"
      cta="Create a free account to try First Step's mock oral board — a three-person AI panel that asks questions by voice and scores your answers. Your first full board is free; unlimited boards with detailed reports are part of Premium."
    >
      <section>
        <h2>Why panels ask what they ask</h2>
        <p>An oral board is not a quiz. Panel members are trying to answer a few practical questions about you: Will this person be safe and dependable on a crew? Do they understand what the job actually involves? Can they communicate clearly with the public and with coworkers under stress? Are they honest when the honest answer is uncomfortable? Nearly every question, however it is worded, is an attempt to get evidence for one of those judgments. Keep that in mind and you will answer the question behind the question.</p>
        <p>If you are new to the format, start with our overview of <Link to="/firefighter-oral-board-prep">firefighter oral board preparation</Link>, then come back here for the questions themselves.</p>
      </section>

      <section>
        <h2>Common firefighter oral board questions by category</h2>
        <p>The examples below are representative of questions candidates commonly report. Wording differs by department, and some panels use scenario formats instead of direct questions.</p>

        <h3>About you and your motivation</h3>
        <ul>
          <li>Tell us about yourself.</li>
          <li>Why do you want to be a firefighter?</li>
          <li>What have you done to prepare for this career?</li>
          <li>What do you think a probationary firefighter does on a typical shift?</li>
        </ul>
        <p>Panels want a clear, honest path — not a speech about heroism. Show that you understand the routine parts of the job (station duties, training, medical calls, public contact) as well as the emergencies.</p>

        <h3>Why this department</h3>
        <ul>
          <li>Why do you want to work for this department specifically?</li>
          <li>What do you know about our community and the area we serve?</li>
          <li>What would you do if you were not selected this time?</li>
        </ul>
        <p>Specifics matter here. Know the department's stations, service area, and anything it says publicly about its values or mission. The free <Link to="/departments">fire department directory</Link> is a starting point for basic facts and official links.</p>

        <h3>Situational and ethical judgment</h3>
        <ul>
          <li>You see a coworker take something small from a patient's home. What do you do?</li>
          <li>Your officer gives you an order you believe is unsafe. How do you respond?</li>
          <li>A crew member repeatedly shows up late and asks you to cover for them. What do you do?</li>
        </ul>
        <p>These questions test whether you will do the right thing when it is awkward. A strong answer names the value at stake (safety, honesty, trust), describes a proportionate first step, and explains when you would escalate through the chain of command.</p>

        <h3>Teamwork and conflict</h3>
        <ul>
          <li>Describe a time you had a conflict with a coworker. How did you handle it?</li>
          <li>How do you handle criticism from a supervisor?</li>
          <li>What role do you usually take on a team?</li>
        </ul>
        <p>Firefighters live and work together for long shifts. Panels listen for humility, directness, and the ability to resolve friction without drama.</p>

        <h3>Community and customer service</h3>
        <ul>
          <li>Describe a time you helped someone who was upset or difficult.</li>
          <li>How would you treat a person who calls 911 for something that is not an emergency?</li>
        </ul>
        <p>Most calls are medical or service calls. Show that you can be patient and respectful with people on their worst day.</p>

        <h3>Self-awareness</h3>
        <ul>
          <li>What is your greatest weakness?</li>
          <li>Tell us about a time you failed. What did you learn?</li>
          <li>Is there anything in your background we should know about?</li>
        </ul>
        <p>Give a real weakness and a real plan. Panels are usually more concerned with candidates who claim to have no flaws than with candidates who own theirs.</p>
      </section>

      <section>
        <h2>How to organize an answer</h2>
        <p>Rambling is the most common way good candidates lose points. A simple three-part structure keeps you on track:</p>
        <ol>
          <li><strong>Direct answer.</strong> One or two sentences that answer exactly what was asked.</li>
          <li><strong>Specific example.</strong> The situation, what you did, and what happened. Use "I", not "we", for the parts you actually did.</li>
          <li><strong>Connection to the job.</strong> One sentence on what the example says about you as a firefighter.</li>
        </ol>
        <p>Aim for roughly one to two minutes per answer. If a panel wants more, they will ask. Pause before you speak — a short breath reads as composure.</p>
      </section>

      <section>
        <h2>Build a story bank before you practice</h2>
        <p>Rather than preparing an answer for every possible question, prepare five to eight real stories that can each answer several questions. A useful set covers:</p>
        <ul>
          <li>A conflict you resolved with a coworker, teammate or customer.</li>
          <li>A time you failed or received hard feedback, and what you changed.</li>
          <li>A time you did the right thing when it cost you something.</li>
          <li>A time you served someone who was frightened, angry or vulnerable.</li>
          <li>A time you led, and a time you followed a decision you disagreed with.</li>
          <li>A time you worked hard toward a long-term goal.</li>
        </ul>
        <p>Write each story in a few lines, then practice telling it in different lengths — thirty seconds, one minute, two minutes.</p>
      </section>

      <section>
        <h2>A practice plan for oral board questions</h2>
        <ul>
          <li><strong>Say answers out loud.</strong> Reading silently does not reveal filler words, run-on sentences or lost threads.</li>
          <li><strong>Practice with interruptions.</strong> Ask a partner to follow up with "Why?" and "What would you do differently?"</li>
          <li><strong>Record and review.</strong> Listen for pace, volume and whether you actually answered the question.</li>
          <li><strong>Rotate categories.</strong> Do not practice only the questions you like.</li>
          <li><strong>Use a mock panel.</strong> First Step's <Link to="/firefighter-mock-oral-board">mock oral board</Link> puts you in front of a three-person AI panel that asks by voice and scores content, structure and delivery. See how it fits into a full plan in <Link to="/firefighter-interview-practice">firefighter interview practice</Link>.</li>
        </ul>
      </section>

      <section>
        <h2>Mistakes to avoid</h2>
        <ul>
          <li>Answering a different question than the one asked.</li>
          <li>Speaking in generalities with no example.</li>
          <li>Criticizing former employers, other departments or other candidates.</li>
          <li>Claiming you have no weaknesses or have never failed.</li>
          <li>Guessing at department facts instead of saying you do not know.</li>
          <li>Ending answers with a trailing "…so, yeah." Finish with a clear sentence.</li>
        </ul>
      </section>
    </SeoPageShell>
  );
}
